/**
 * submissionConfirmation.js
 * -------------------------
 * Business rules for the respondent "Confirm and submit" step (TDI-415) and
 * the per-respondent answer lock it creates.
 *
 * After their last form, each parent or carer and each school user confirms
 * they have finished. Confirming locks that respondent's answers (enforced
 * server-side in rmsSaveFormDraft / rmsSubmitForm — service users cannot
 * write form instances from the client at all), and on the referral pathway
 * the final confirmation is what submits the referral and stamps the
 * referral received date (TDI-418, triageReadiness.markedAt).
 *
 * This module mirrors the Assessment platform's formLockingUtils.js pattern:
 * pure rules, zero Firebase, zero mocks to test. The lock trigger differs —
 * Assessment forms lock when the clinician starts the assessment; RMS forms
 * lock when the respondent themselves confirms — so the rules live here, in
 * RMS scope, rather than being bolted onto the Assessment module.
 *
 * The server-side twin is _role_required_forms_complete in rms_forms.py —
 * keep the two in step, or the portal offers a confirm the server refuses.
 */

import {
  isReferralWithdrawn,
  isRoleReferralFormsComplete,
  areAllTriageFormsComplete,
  hfpFormKeysForRecord,
  isHarbourPupilRecord,
  isRoleSubmissionConfirmed,
  isTriageFormInstanceKey,
  instancePathway,
} from './referralStatus';

export {
  submissionConfirmations,
  isRoleSubmissionConfirmed,
  allRequiredRespondentsConfirmed,
  hfpReportConfirmationsIn,
  instancePathway,
  recordHasSenPathway,
  recordRequiresSubmissionConfirmation,
  SUBMISSION_CONFIRMATION_CUTOVER_MS,
} from './referralStatus';

/*
 * Per-pathway confirmations (TDI-651). The SEND pathway and the referral
 * pathway are completely separate (Tom King, 27 October 2026), so each
 * respondent confirms each pathway on its own, each confirmation locks only
 * its own pathway's forms, the referral is submitted by the referral's
 * confirmations and the strengths and needs profile by the SEND pathway's.
 * Every rule below takes the pathway; where a caller does not say, the
 * referral wherever the record has one, else the SEND pathway — what the
 * single confirm meant before.
 */

/**
 * Whether this record carries a referral pathway (as opposed to a Harbour
 * SEN-only record, whose respondents are judged on the HFP alone). Every
 * non-Harbour record is a referral record by construction; a Harbour pupil is
 * on the referral pathway once a concern is logged or the pathway has
 * started. Mirrors the backend's referral_active rule.
 */
export function isReferralPathwayActive(patient) {
  if (!isHarbourPupilRecord(patient)) return true;
  return Boolean(patient?.intakeConcern) || Boolean(patient?.referralPathway?.startedAt);
}

/**
 * Whether the school has this child on the SEND pathway as well.
 *
 * The two pathways are completely separate (Tom King, 27 October 2026), so
 * when both are running the strengths and needs forms belong to the SEND
 * pathway and finish on its own timeline. A referral must never wait for
 * them. On a referral-only child the same forms are part of the referral and
 * still gate it, which is why this asks about the SEND pathway rather than
 * about the forms.
 *
 * A stopped SEND pathway is not running, so it gates nothing either way.
 * Mirrors senStage in harbourPupils, kept local to avoid an import cycle
 * between the two modules.
 */
export function isSenPathwayActive(patient) {
  const sen = patient?.senPathway || null;
  if (!sen || !sen.status || sen.status === 'not_active') return false;
  return sen.status !== 'stopped';
}

/**
 * Whether the family has taken their permission for the SEND pathway back
 * (TDI-479). Mirrors is_sen_withdrawn in rms_consent_withdrawal.py.
 */
export function isSenWithdrawn(patient) {
  const sen = patient?.senPathway || {};
  return sen.status === 'stopped' && sen.stopReason === 'consent_withdrawn';
}

/**
 * The pathway a confirmation is for when the caller does not say: the
 * referral wherever there is one, else the SEND pathway. Mirrors
 * _default_confirm_pathway in rms_forms.py.
 *
 * @returns {'referral'|'sen'}
 */
export function defaultConfirmPathway(patient) {
  return isReferralPathwayActive(patient) ? 'referral' : 'sen';
}

/**
 * Whether one respondent has submitted every form one pathway asks of them —
 * the gate for offering that respondent the pathway's confirm-and-submit.
 *
 * 'referral': the base referral forms (isRoleReferralFormsComplete) plus,
 * for the guardian only, the assigned triage forms; false when the record
 * has no referral. The strengths and needs forms are not among them — they
 * belong to the SEND pathway and to no referral (TDI-495).
 * 'sen': the respondent's strengths and needs set, which is the one place
 * the instrument choice still decides anything.
 *
 * @param {object} args
 * @param {object|null} args.patient - patientRecords doc data
 * @param {Record<string, number>} args.formProgress - instanceKey → percent,
 *   built through buildFormProgressFromRecord/Snapshot (100 means submitted)
 * @param {boolean} [args.triageAssigned] - triage instances detected in the
 *   progress fold (buildFormProgress* returns it beside the map)
 * @param {'guardian'|'school_user'} args.role
 * @param {'referral'|'sen'} [args.pathway] - defaults to defaultConfirmPathway
 * @returns {boolean}
 */
export function hasRoleFinishedAllForms({ patient, formProgress, triageAssigned, role, pathway }) {
  if (role !== 'guardian' && role !== 'school_user') return false;
  const which = pathway || defaultConfirmPathway(patient);
  if (which === 'referral') {
    if (!isReferralPathwayActive(patient)) return false;
    const intakeConcern = patient?.intakeConcern || null;
    // TDI-495: the strengths and needs forms are the SEND pathway's work and
    // never hold up a referral. This asked about the SEND pathway until 28
    // October, which freed a child on both and left a referral-only child
    // still waiting on forms that were never part of their referral. The
    // question a referral has is simply whether its own forms are in.
    if (!isRoleReferralFormsComplete(role, formProgress, intakeConcern)) {
      return false;
    }
    if (role === 'guardian') {
      const assigned = Boolean(patient?.triageFormsAssigned)
        || Boolean(triageAssigned)
        || Object.keys(formProgress || {}).some(isTriageFormInstanceKey);
      return assigned && areAllTriageFormsComplete(formProgress);
    }
    return true;
  }
  const keys = hfpFormKeysForRecord(patient);
  if (!keys.length) return false;
  return keys.every((key) => Number(formProgress?.[`${key}_${role}`]) >= 100);
}

/**
 * Whether to offer this respondent a pathway's "Confirm and submit" step now:
 * all of their own forms on that pathway are in and they have not already
 * confirmed it. The other respondent's progress never gates the offer — each
 * respondent confirms their own forms (TDI-384) — and neither does the other
 * pathway (TDI-651).
 */
export function canConfirmSubmission({ patient, formProgress, triageAssigned, role, pathway }) {
  const which = pathway || defaultConfirmPathway(patient);
  // Locked covers every reason not to offer it: already confirmed, or the
  // pathway has been withdrawn. Offering "Confirm and submit" on a stopped
  // pathway would promise the family something that cannot happen.
  if (areRoleAnswersLocked(patient, role, which)) return false;
  // The SEND pathway's confirm needs the pathway running, as the server does
  // (_sen_pathway_running): a SEN-only pupil with no status yet counts.
  if (which === 'sen' && !isSenPathwayActive(patient)
      && !(!patient?.senPathway?.status && !isReferralPathwayActive(patient))) {
    return false;
  }
  return hasRoleFinishedAllForms({ patient, formProgress, triageAssigned, role, pathway: which });
}

/**
 * Whether a referral's consent has been withdrawn and not since restored.
 *
 * The client mirror of is_referral_withdrawn in rms_consent_withdrawal.py —
 * keep the two in step, or a surface offers an edit the server refuses. An
 * explicit restoration (TDI-478) is the last word; a pathway stopped for any
 * other reason is not a withdrawal.
 */
// The predicate itself now lives in referralStatus.js, where the dashboard's
// stage classifier needs it too (TDI-481); it is re-exported here so every
// existing import keeps working.
export { isReferralWithdrawn };

/**
 * Whether this respondent's answers on one pathway are locked. The server
 * refuses writes regardless; surfaces read this to remove the edit
 * affordances rather than let someone fill in a form and be turned away at
 * the end of it.
 *
 * Reasons: the respondent confirmed and submitted that pathway (TDI-415,
 * per pathway since TDI-651), the family withdrew consent from the referral
 * (TDI-472/474) — which stops every form for both respondents, because
 * nobody should go on answering questions for a referral that has stopped —
 * or, for the SEND pathway, the family withdrew from it (TDI-479).
 *
 * @param {object|null} patient
 * @param {'guardian'|'school_user'} role
 * @param {'referral'|'sen'} [pathway] - defaults to defaultConfirmPathway
 */
export function areRoleAnswersLocked(patient, role, pathway) {
  const which = pathway || defaultConfirmPathway(patient);
  if (isReferralWithdrawn(patient)) return true;
  if (which === 'sen' && isSenWithdrawn(patient)) return true;
  return isRoleSubmissionConfirmed(patient, role, which);
}

/**
 * Whether this one form's answers are locked. The form's own pathway's
 * confirmation is the one that counts (instancePathway): confirming the
 * referral never locks the SEND pathway's forms, nor the reverse. Within a
 * pathway a confirmation covers the forms submitted when the respondent
 * confirmed, not forms that arrive later. A withdrawn referral locks every
 * form, and a withdrawn SEND pathway its own.
 *
 * The client twin of _answers_lock_message in rms_forms.py — keep in step.
 *
 * @param {object|null} patient - patientRecords doc data (formProgress included)
 * @param {'guardian'|'school_user'} role
 * @param {string} instanceId - e.g. 'hfpV3ExecutiveFunction_school_user'
 */
export function areInstanceAnswersLocked(patient, role, instanceId) {
  if (isReferralWithdrawn(patient)) return true;
  const pathway = instancePathway(patient, instanceId);
  if (pathway === 'sen' && isSenWithdrawn(patient)) return true;
  if (!isRoleSubmissionConfirmed(patient, role, pathway)) return false;
  const status = ((patient?.formProgress || {})[instanceId] || {}).status;
  return status === 'submitted' || status === 'completed';
}
